import type { BranchKey, WorkspaceKey } from '@festina-life/api'
import { ErrorMessage } from '@festina-life/components'
import type { SetRequiredAndNonNullable } from '@festina-life/core'
import { Alert } from '@festina-life/ui/feedback'
import { Checkbox } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { SimpleModal, Stack } from '@festina-life/ui/structure'
import { useEffect, useId, useState } from 'react'

import { useAllConciseBranches } from '../../../../uiModels/branches/useAllConciseBranches'
import type { ConciseChangesetWithBranchKey } from '../../../../uiModels/changesets/types'
import type { WorkspaceCreateOrUpdateInput } from '../../../../uiModels/workspace/types'
import { useAutomaticWorkspaceName } from '../../../../uiModels/workspace/useAutomaticWorkspaceName'
import { usePullChangesetToWorkspace } from '../../../../uiModels/workspace/usePullChangesetToWorkspace'
import { BranchNameAndCategory } from '../../../branchNameWithStatus/BranchNameAndCategory'
import { WorkspaceForm } from '../../../workspaceCreationModal/WorkspaceForm'
import { slugifyChangesetName } from './helpers'

type Props = {
  isOpen: boolean
  onCancel: () => void
  onPulled: (workspaceKey: WorkspaceKey) => void
  branchKey: BranchKey
  changeset: SetRequiredAndNonNullable<
    ConciseChangesetWithBranchKey,
    'changesetKey'
  >
}

export const PullChangesetToWorkspaceModal = ({
  isOpen,
  onCancel,
  onPulled,
  branchKey,
  changeset,
}: Props) => {
  const translate = useTranslate()
  const formId = useId()
  const allConciseBranches = useAllConciseBranches()
  const { mutateAsync, isPending, error, reset } =
    usePullChangesetToWorkspace(branchKey)
  const [shouldForce, setShouldForce] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setShouldForce(false)
      reset()
    }
  }, [isOpen, reset])

  const dependentBranches = (allConciseBranches[branchKey.server] ?? []).filter(
    ({ name, baseBranchName, followsBranchName }) =>
      name !== branchKey.name &&
      (baseBranchName === branchKey.name ||
        followsBranchName === branchKey.name),
  )
  const baseName = slugifyChangesetName(
    changeset.description ?? changeset.changesetKey ?? '',
  )
  const initialName = useAutomaticWorkspaceName({
    workspaceNamePrefix: baseName,
    server: branchKey.server,
  })

  const initialValues = {
    name: initialName,
    branchKey,
  } satisfies WorkspaceCreateOrUpdateInput

  const handleSubmit = async ({ name }: WorkspaceCreateOrUpdateInput) => {
    try {
      await mutateAsync({
        changesetKey: changeset.changesetKey,
        workspaceName: name,
        force: shouldForce,
      })
      onPulled({ name, branchKey })
    } catch {
      // The error is surfaced to the user via the mutation's `error` state.
    }
  }

  return (
    <SimpleModal
      title={translate('changesetList.pullToWorkspace.title')}
      isOpen={isOpen}
      acceptConfiguration={{
        formId,
        label: translate('changesetList.pullToWorkspace.confirm'),
        loading: isPending,
        disabled: dependentBranches.length > 0 && !shouldForce,
      }}
      closeConfiguration={{ onClose: onCancel, label: translate('cancel') }}
      bodyOuterClassName="w-md p-2 "
      bodyInnerClassName="gap-y-4"
    >
      <ErrorMessage error={error} />
      <p>{translate('changesetList.pullToWorkspace.description')}</p>
      <WorkspaceForm
        initialValues={initialValues}
        onSubmit={handleSubmit}
        formId={formId}
      />
      {dependentBranches.length > 0 && (
        <>
          <Alert
            severity="info"
            title={translate(
              'changesetList.pullToWorkspace.dependentBranchesWarning',
            )}
          >
            <Stack className="flex-wrap mt-2" gap="display">
              {dependentBranches.map((branch) => (
                <BranchNameAndCategory
                  key={branch.name}
                  specification={branch}
                />
              ))}
            </Stack>
          </Alert>
          <Checkbox
            checked={shouldForce}
            onChange={setShouldForce}
            wrapperProps={{
              label: translate('changesetList.pullToWorkspace.force.label'),
              description: translate(
                'changesetList.pullToWorkspace.force.description',
              ),
            }}
          />
        </>
      )}
    </SimpleModal>
  )
}
